import { Player } from './Player';
import { PowerUp } from './PowerUp';
import { Position } from '../types/GameTypes';

export type AIPersonality = 'aggressive' | 'defensive' | 'opportunist' | 'chaotic';

export class AIBot extends Player {
  public personality: AIPersonality;
  private lastDecisionTime: number = 0;
  private decisionCooldown: number = 300; // 300ms between decisions
  private aiLastJumpTime: number = 0;
  private aiJumpCooldown: number = 500;
  
  constructor(id: string, name: string, color: string, startPosition: Position, personality: AIPersonality = 'opportunist') {
    super(id, name, color, startPosition);
    this.personality = personality;
    this.setupPersonality();
  }

  private setupPersonality(): void {
    switch (this.personality) {
      case 'aggressive':
        this.decisionCooldown = 200; // Fast decisions
        this.aiJumpCooldown = 300; // Frequent jumping
        break;
      case 'defensive':
        this.decisionCooldown = 500; // Slower, more thoughtful
        this.aiJumpCooldown = 800; // Conservative jumping
        break;
      case 'opportunist':
        this.decisionCooldown = 300; // Balanced
        this.aiJumpCooldown = 500; // Balanced
        break;
      case 'chaotic':
        this.decisionCooldown = 100 + Math.random() * 400; // Random timing
        this.aiJumpCooldown = 200 + Math.random() * 600; // Random jumping
        break;
    }
  }

  update(allPlayers: Player[], powerups: PowerUp[], areaWidth: number, areaHeight: number): void {
    if (!this.alive) return;

    const now = Date.now();
    if (now - this.lastDecisionTime < this.decisionCooldown) return;

    this.lastDecisionTime = now;
    
    // Update personality-specific decision cooldown for chaotic bots
    if (this.personality === 'chaotic') {
      this.decisionCooldown = 100 + Math.random() * 400;
    }

    this.makeDecision(allPlayers, powerups, areaWidth, areaHeight);
  }

  private makeDecision(allPlayers: Player[], powerups: PowerUp[], areaWidth: number, areaHeight: number): void {
    const nearbyPlayers = this.getNearbyPlayers(allPlayers, 200);
    const nearbyPowerups = this.getNearbyPowerups(powerups, 150);
    
    // Assess threats and opportunities
    const threats = this.assessThreats(nearbyPlayers);
    const opportunities = this.assessOpportunities(nearbyPowerups);
    
    // Make decision based on personality
    switch (this.personality) {
      case 'aggressive':
        this.aggressiveBehavior(threats, opportunities, areaWidth, areaHeight);
        break;
      case 'defensive':
        this.defensiveBehavior(threats, opportunities, areaWidth, areaHeight);
        break;
      case 'opportunist':
        this.opportunistBehavior(threats, opportunities, areaWidth, areaHeight);
        break;
      case 'chaotic':
        this.chaoticBehavior(allPlayers, powerups, areaWidth, areaHeight);
        break;
    }
  }

  private aggressiveBehavior(threats: Player[], opportunities: PowerUp[], areaWidth: number, areaHeight: number): void {
    // Prioritize attacking nearby players
    if (threats.length > 0) {
      const target = threats[0]; // Attack closest threat
      this.moveTowardsTarget(target.position, true);
    } else if (opportunities.length > 0) {
      // Go for powerups only if no players nearby
      const targetPowerup = this.selectBestPowerup(opportunities);
      if (targetPowerup) {
        this.moveTowardsTarget(targetPowerup.position, false);
      }
    } else {
      // Hunt for players
      this.huntForPlayers(areaWidth, areaHeight);
    }
  }

  private defensiveBehavior(threats: Player[], opportunities: PowerUp[], areaWidth: number, areaHeight: number): void {
    // Avoid threats, prioritize safety and powerups
    if (threats.length > 0 && this.health < 70) {
      // Escape from threats when low health
      this.escapeFromThreats(threats, areaWidth, areaHeight);
    } else if (opportunities.length > 0) {
      // Safe powerup collection
      const safePowerup = this.selectSafestPowerup(opportunities, threats);
      if (safePowerup) {
        this.moveTowardsTarget(safePowerup.position, false);
      }
    } else {
      // Stay in safe positions
      this.moveToSafePosition(areaWidth, areaHeight);
    }
  }

  private opportunistBehavior(threats: Player[], opportunities: PowerUp[], areaWidth: number, areaHeight: number): void {
    // Balanced approach - assess risk vs reward
    const healthRatio = this.health / this.maxHealth;
    
    if (opportunities.length > 0 && (threats.length === 0 || healthRatio > 0.5)) {
      // Go for powerups when safe or healthy
      const targetPowerup = this.selectBestPowerup(opportunities);
      if (targetPowerup) {
        this.moveTowardsTarget(targetPowerup.position, false);
      }
    } else if (threats.length > 0 && healthRatio > 0.7) {
      // Engage when healthy
      const target = threats.find(p => p.health < this.health) || threats[0];
      this.moveTowardsTarget(target.position, true);
    } else if (threats.length > 0) {
      // Defensive when weak
      this.escapeFromThreats(threats, areaWidth, areaHeight);
    } else {
      // Patrol for opportunities
      this.patrol(areaWidth, areaHeight);
    }
  }

  private chaoticBehavior(allPlayers: Player[], powerups: PowerUp[], areaWidth: number, areaHeight: number): void {
    // Random behavior with some logic
    const randomAction = Math.random();
    
    if (randomAction < 0.4 && allPlayers.length > 1) {
      // Random player targeting
      const alivePlayers = allPlayers.filter(p => p.alive && p.id !== this.id);
      if (alivePlayers.length > 0) {
        const randomTarget = alivePlayers[Math.floor(Math.random() * alivePlayers.length)];
        this.moveTowardsTarget(randomTarget.position, true);
      }
    } else if (randomAction < 0.7 && powerups.length > 0) {
      // Random powerup targeting
      const randomPowerup = powerups[Math.floor(Math.random() * powerups.length)];
      this.moveTowardsTarget(randomPowerup.position, false);
    } else {
      // Random movement
      const randomPos = {
        x: Math.random() * areaWidth,
        y: Math.random() * areaHeight
      };
      this.moveTowardsTarget(randomPos, Math.random() < 0.3);
    }
  }

  private moveTowardsTarget(targetPos: Position, aggressive: boolean): void {
    const distance = this.distanceFrom(targetPos);
    if (distance < 50) return; // Already close enough

    const now = Date.now();
    if (now - this.aiLastJumpTime < this.aiJumpCooldown) return;

    // Calculate direction to target
    const dx = targetPos.x - this.position.x;
    const dy = targetPos.y - this.position.y;
    const magnitude = Math.sqrt(dx * dx + dy * dy);

    if (magnitude === 0) return;

    const direction = {
      x: dx / magnitude,
      y: dy / magnitude
    };

    // Adjust jump based on situation
    if (aggressive && distance < 100) {
      // Aggressive jump towards target
      direction.y = -0.8; // More upward for impact
    } else if (distance > 150) {
      // Long distance jump
      direction.y = -0.5; // Balanced trajectory
    } else {
      // Normal approach
      direction.y = -0.6;
    }

    if (this.jump(direction)) {
      this.aiLastJumpTime = now;
    }
  }

  private escapeFromThreats(threats: Player[], _areaWidth: number, _areaHeight: number): void {
    // Calculate escape direction away from threats
    let escapeX = 0;
    let escapeY = 0;

    threats.forEach(threat => {
      const dx = this.position.x - threat.position.x;
      const dy = this.position.y - threat.position.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance > 0) {
        escapeX += dx / distance;
        escapeY += dy / distance;
      }
    });

    // Normalize escape direction
    const magnitude = Math.sqrt(escapeX * escapeX + escapeY * escapeY);
    if (magnitude > 0) {
      escapeX /= magnitude;
      escapeY /= magnitude;
    }

    this.moveTowardsTarget({
      x: this.position.x + escapeX * 200,
      y: this.position.y + escapeY * 100
    }, false);
  }

  private getNearbyPlayers(allPlayers: Player[], radius: number): Player[] {
    return allPlayers.filter(player => 
      player.alive && 
      player.id !== this.id && 
      this.distanceFrom(player.position) <= radius
    );
  }

  private getNearbyPowerups(powerups: PowerUp[], radius: number): PowerUp[] {
    return powerups.filter(powerup => 
      this.distanceFrom(powerup.position) <= radius
    );
  }

  private assessThreats(nearbyPlayers: Player[]): Player[] {
    // Sort by threat level (closer + higher health = more threatening)
    return nearbyPlayers.sort((a, b) => {
      const distA = this.distanceFrom(a.position);
      const distB = this.distanceFrom(b.position);
      const threatA = (a.health / a.maxHealth) / (distA + 1);
      const threatB = (b.health / b.maxHealth) / (distB + 1);
      return threatB - threatA;
    });
  }

  private assessOpportunities(nearbyPowerups: PowerUp[]): PowerUp[] {
    // Sort by value and accessibility
    return nearbyPowerups.sort((a, b) => {
      const distA = this.distanceFrom(a.position);
      const distB = this.distanceFrom(b.position);
      const valueA = this.getPowerupValue(a.type);
      const valueB = this.getPowerupValue(b.type);
      
      return (valueB / (distB + 1)) - (valueA / (distA + 1));
    });
  }

  private getPowerupValue(type: PowerUp['type']): number {
    switch (type) {
      case 'health': return this.health < 50 ? 10 : 3; // Higher value when low health
      case 'shield': return 8;
      case 'gravity-flip': return 6;
      case 'mega-jump': return 4;
      case 'boost': return 3;
      default: return 1;
    }
  }

  private selectBestPowerup(powerups: PowerUp[]): PowerUp | null {
    if (powerups.length === 0) return null;
    return powerups[0]; // Already sorted by value
  }

  private selectSafestPowerup(powerups: PowerUp[], threats: Player[]): PowerUp | null {
    // Find powerup that's farthest from threats
    let safestPowerup: PowerUp | null = null;
    let maxSafetyScore = -1;

    powerups.forEach(powerup => {
      let safetyScore = this.getPowerupValue(powerup.type);
      
      // Reduce score based on nearby threats
      threats.forEach(threat => {
        const threatDistance = threat.distanceFrom(powerup.position);
        if (threatDistance < 100) {
          safetyScore -= (100 - threatDistance) / 10;
        }
      });

      if (safetyScore > maxSafetyScore) {
        maxSafetyScore = safetyScore;
        safestPowerup = powerup;
      }
    });

    return safestPowerup;
  }

  private moveToSafePosition(areaWidth: number, areaHeight: number): void {
    // Move towards center or away from edges
    const centerX = areaWidth / 2;
    const centerY = areaHeight / 2;
    
    if (this.distanceFrom({ x: centerX, y: centerY }) > 100) {
      this.moveTowardsTarget({ x: centerX, y: centerY }, false);
    }
  }

  private huntForPlayers(areaWidth: number, areaHeight: number): void {
    // Move in search patterns
    const searchTarget = {
      x: areaWidth * (0.2 + Math.random() * 0.6),
      y: areaHeight * (0.2 + Math.random() * 0.6)
    };
    this.moveTowardsTarget(searchTarget, false);
  }

  private patrol(areaWidth: number, areaHeight: number): void {
    // Simple patrol pattern
    const patrolTarget = {
      x: areaWidth * (0.3 + Math.random() * 0.4),
      y: areaHeight * (0.3 + Math.random() * 0.4)
    };
    this.moveTowardsTarget(patrolTarget, false);
  }

  static createAIBot(index: number, startPosition: Position): AIBot {
    const personalities: AIPersonality[] = ['aggressive', 'defensive', 'opportunist', 'chaotic'];
    const names = ['AggroBot', 'DefenseBot', 'OpportunBot', 'ChaosBot'];
    const colors = ['#FF4444', '#4444FF', '#44FF44', '#FF44FF'];
    
    const personality = personalities[index % personalities.length];
    const name = names[index % names.length] + (index >= 4 ? ` ${Math.floor(index / 4) + 1}` : '');
    const color = colors[index % colors.length];
    
    return new AIBot(
      `ai-bot-${index}-${Date.now()}`,
      name,
      color,
      startPosition,
      personality
    );
  }
}